/**
 * OAuth Provider 登入方法
 * 負責 Google / LINE / Facebook / Email 登入
 */

import { supabase } from '@shared/services/core/supabaseClient'
import type { Provider } from '@supabase/supabase-js'
import { type AuthUser, type ProviderName, convertSupabaseUserToAuthUser } from './types'
import { userProfileService } from '../user/userProfileService'

function resolveProvider(providerName: ProviderName): Provider {
  const providerMap: Record<string, Provider> = {
    google: 'google',
    facebook: 'facebook',
    line: 'line' as Provider,
    'oidc.line': 'line' as Provider,
    'google.com': 'google',
    'facebook.com': 'facebook',
  }
  return providerMap[providerName] || (providerName as Provider)
}

// ============ OAuth 登入 ============

export async function signInWithGoogle(): Promise<AuthUser | null> {
  return signInWithProvider('google')
}

export async function signInWithLine(): Promise<AuthUser | null> {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  if (isMobile) {
    return signInWithRedirect('line')
  } else {
    return signInWithProvider('line')
  }
}

export async function signInWithFacebook(): Promise<AuthUser | null> {
  return signInWithProvider('facebook')
}

export async function signInWithProvider(providerName: ProviderName): Promise<AuthUser | null> {
  const supabaseProvider = resolveProvider(providerName)

  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: supabaseProvider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    })

    if (error) throw error

    // OAuth 登入會重定向，這裡不會返回用戶
    return null
  } catch (error) {
    console.error('[OAuthProviders] Login error:', error)
    throw error
  }
}

export async function signInWithRedirect(providerName: ProviderName): Promise<AuthUser | null> {
  const supabaseProvider = resolveProvider(providerName)

  localStorage.setItem(
    'auth_redirect_state',
    JSON.stringify({ provider: providerName })
  )

  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: supabaseProvider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    })

    if (error) throw error

    // OAuth 重定向會離開頁面
    return null
  } catch (error) {
    console.error('[OAuthProviders] Redirect login error:', error)
    throw error
  }
}

// ============ Email/密碼登入 ============

export async function signUp(email: string, password: string): Promise<AuthUser> {
  const { data, error } = await supabase.auth.signUp({ email, password })

  if (error) throw error

  if (data.user) {
    // Profile 同步失敗不應阻擋登入（fire-and-warn）
    userProfileService.createOrUpdateUserProfile(data.user).catch(err => {
      console.warn('[OAuthProviders] 用戶資料同步失敗:', err)
    })
    return convertSupabaseUserToAuthUser(data.user) as AuthUser
  }

  throw new Error('註冊失敗：未返回用戶資料')
}

export async function signInWithPassword(email: string, password: string): Promise<AuthUser> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })

  if (error) throw error

  if (data.user) {
    // Profile 同步失敗不應阻擋登入（fire-and-warn）
    userProfileService.createOrUpdateUserProfile(data.user).catch(err => {
      console.warn('[OAuthProviders] 用戶資料同步失敗:', err)
    })
    return convertSupabaseUserToAuthUser(data.user) as AuthUser
  }

  throw new Error('登入失敗：未返回用戶資料')
}
